import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { PageService } from 'src/app/core/page/page.service';
import { selectColorTheme } from 'src/app/store/color-theme/selectors';
import { selectActiveLanguage } from 'src/app/store/language/selectors';
import { AppStateInterface } from 'src/app/store/types';
import { NgOptimizedImage, AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';

@Component({
    selector: 'app-about',
    templateUrl: './about.component.html',
    styleUrls: ['./about.component.scss'],
    imports: [
        NgOptimizedImage,
        AsyncPipe,
        TranslateModule,
    ]
})
export class AboutComponent {
  activeTheme$: Observable<string>;
  activeLanguage: string = '';
  languageDirection: string = 'ltr'


  constructor(
    private _pageService: PageService,
    private _store: Store<AppStateInterface>,
    private activatedRoute: ActivatedRoute,
    private _router: Router,
    private titleService: Title,
    private metaService: Meta
  ) {
    this.activeTheme$ = this._store.select(selectColorTheme);
  }

  ngOnInit() {
    this._store.select(selectActiveLanguage).subscribe({
      next: (activeLanguage) => {
        this.activeLanguage = activeLanguage;
        this.languageDirection = this._pageService.getLanguageDirection(activeLanguage)
      },
    });
    this.initSEOContent();
  }

  ngAfterViewInit() {
    this._pageService.setDirection(this.activeLanguage);
    // this.initBenefitsIntersectionObserver();
  }

  public initSEOContent() {
    const urlFragments = this._router.url.split('/');
    if (urlFragments && urlFragments.length > 1 && ['en', 'fa', 'de', 'id'].includes(urlFragments[1])) {
      const language = urlFragments[1];
      this.activatedRoute.data.subscribe(() => {
        // Common meta tags
        const imageUrl = 'https://almanjobs-strapi-public-bucket.s3-eu-central-2.ionoscloud.com/almanjobs_logo_primary_98d1b23517.svg';
        this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
        this.metaService.updateTag({ name: 'viewport', content: 'width=device-width, initial-scale=1.0' });
        this.metaService.updateTag({ name: 'author', content: 'Almanjobs' });
        this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.metaService.updateTag({ name: 'og:image', content: imageUrl });
        this.metaService.updateTag({ name: 'twitter:image', content: imageUrl });
        this.metaService.addTag({ rel: 'alternate', hreflang: 'x-default', href: `https://almanjobs.com/en/about-us` });

        // Language-specific meta tags
        if (language === 'en') {
          this.titleService.setTitle('About Almanjobs - Vision and Mission');
          this.metaService.updateTag({
            name: 'description',
            content: 'Learn about Almanjobs: our story, mission, and vision to help skilled workers find jobs and migrate comfortably to Germany.',
          });
          this.metaService.updateTag({
            name: 'keywords',
            content: 'Almanjobs story, about Almanjobs, skilled workers Germany, job migration Germany, vision Almanjobs',
          });
          this.metaService.updateTag({
            property: 'og:title',
            content: 'About Almanjobs - Vision and Mission',
          });
          this.metaService.updateTag({
            property: 'og:description',
            content: 'Discover how Almanjobs began, our mission to support skilled workers, and our vision for seamless migration to Germany.',
          });
          this.metaService.updateTag({
            property: 'og:url',
            content: `https://almanjobs.com/en/about-us`,
          });
          this.metaService.addTag({ rel: 'alternate', hreflang: 'en', href: `https://almanjobs.com/en/about-us` });
        }

        if (language === 'de') {
          this.titleService.setTitle('Über Almanjobs - Vision und Mission');
          this.metaService.updateTag({
            name: 'description',
            content: 'Erfahren Sie mehr über Almanjobs: unsere Geschichte, Mission und Vision, um Fachkräften bei der Jobsuche und einer komfortablen Migration nach Deutschland zu helfen.',
          });
          this.metaService.updateTag({
            name: 'keywords',
            content: 'Almanjobs Geschichte, über Almanjobs, Fachkräfte Deutschland, Jobmigration Deutschland, Vision Almanjobs',
          });
          this.metaService.updateTag({
            property: 'og:title',
            content: 'Über Almanjobs - Vision und Mission',
          });
          this.metaService.updateTag({
            property: 'og:description',
            content: 'Entdecken Sie, wie Almanjobs entstanden ist, unsere Mission zur Unterstützung von Fachkräften und unsere Vision für eine nahtlose Migration nach Deutschland.',
          });
          this.metaService.updateTag({
            property: 'og:url',
            content: `https://almanjobs.com/de/about-us`,
          });
          this.metaService.addTag({ rel: 'alternate', hreflang: 'de', href: `https://almanjobs.com/de/about-us` });
        }

        if (language === 'fa') {
          this.titleService.setTitle('درباره Almanjobs - چشم‌انداز و هدف');
          this.metaService.updateTag({
            name: 'description',
            content: 'درباره Almanjobs بیشتر بدانید: داستان ما، مأموریت و چشم‌انداز ما برای کمک به نیروهای ماهر در پیدا کردن شغل و مهاجرت راحت به آلمان.',
          });
          this.metaService.updateTag({
            name: 'keywords',
            content: 'داستان Almanjobs درباره Almanjobs نیروهای ماهر آلمان، مهاجرت شغلی به آلمان، چشم‌انداز Almanjobs',
          });
          this.metaService.updateTag({
            property: 'og:title',
            content: 'درباره Almanjobs - چشم‌انداز و مأموریت',
          });
          this.metaService.updateTag({
            property: 'og:description',
            content: 'با Almanjobs آشنا شوید: چگونه شروع شد، مأموریت ما برای حمایت از نیروهای ماهر و چشم‌انداز ما برای مهاجرت آسان به آلمان.',
          });
          this.metaService.updateTag({
            property: 'og:url',
            content: `https://almanjobs.com/fa/about-us`,
          });
          this.metaService.addTag({ rel: 'alternate', hreflang: 'fa', href: `https://almanjobs.com/fa/about-us` });
        }
      });
    }
  }

}
